import { v4 as uuidv4 } from 'uuid';

// CSRF Token Management
class CSRFTokenManager {
  private static instance: CSRFTokenManager;
  private token: string | null = null;
  private tokenExpiry: number = 0;
  private readonly TOKEN_LIFETIME = 60 * 60 * 1000; // 1 hour
  private readonly STORAGE_KEY = 'tahta_csrf_token';
  private readonly HEADER_NAME = 'X-CSRF-Token';

  private constructor() {
    this.loadToken();
  }

  static getInstance(): CSRFTokenManager {
    if (!CSRFTokenManager.instance) {
      CSRFTokenManager.instance = new CSRFTokenManager();
    }
    return CSRFTokenManager.instance;
  }

  private loadToken(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const { token, expiry } = JSON.parse(stored);
        if (expiry > Date.now()) {
          this.token = token;
          this.tokenExpiry = expiry;
        } else {
          this.clearToken();
        }
      }
    } catch (error) {
      console.error('Failed to load CSRF token:', error);
      this.clearToken();
    }
  }

  private saveToken(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
        token: this.token,
        expiry: this.tokenExpiry
      }));
    } catch (error) {
      console.error('Failed to save CSRF token:', error);
    }
  }

  private clearToken(): void {
    this.token = null;
    this.tokenExpiry = 0;
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear CSRF token:', error);
    }
  }

  public generateToken(): string {
    this.token = uuidv4();
    this.tokenExpiry = Date.now() + this.TOKEN_LIFETIME;
    this.saveToken();
    return this.token;
  }

  public getToken(): string | null {
    if (!this.token || this.tokenExpiry <= Date.now()) {
      return this.generateToken();
    }
    return this.token;
  }

  public validateToken(token: string): boolean {
    if (!this.token || this.tokenExpiry <= Date.now()) {
      return false;
    }
    return this.token === token;
  }

  public refreshToken(): string {
    return this.generateToken();
  }

  public getHeaderName(): string {
    return this.HEADER_NAME;
  }

  // Add CSRF token to request headers
  public addToHeaders(headers: HeadersInit = {}): HeadersInit {
    const token = this.getToken();
    if (token) {
      if (headers instanceof Headers) {
        headers.set(this.HEADER_NAME, token);
      } else if (Array.isArray(headers)) {
        headers.push([this.HEADER_NAME, token]);
      } else {
        headers[this.HEADER_NAME] = token;
      }
    }
    return headers;
  }
}

// Export singleton instance
export const csrfTokenManager = CSRFTokenManager.getInstance();

// CSRF Protection Middleware for API Client
export function withCSRFProtection<T extends (...args: any[]) => Promise<any>>(
  fn: T
): T {
  return (async (...args: Parameters<T>) => {
    // Add CSRF token to request if it has headers
    if (args[0] && typeof args[0] === 'object' && 'headers' in args[0]) {
      args[0].headers = csrfTokenManager.addToHeaders(args[0].headers);
    }
    
    try {
      const result = await fn(...args);
      return result;
    } catch (error: any) {
      // If CSRF token is invalid, refresh and retry once
      if (error?.response?.status === 403 && error?.response?.data?.error === 'Invalid CSRF token') {
        csrfTokenManager.refreshToken();
        
        // Retry with new token
        if (args[0] && typeof args[0] === 'object' && 'headers' in args[0]) {
          args[0].headers = csrfTokenManager.addToHeaders(args[0].headers);
        }
        
        return await fn(...args);
      }
      throw error;
    }
  }) as T;
}

// Double Submit Cookie Pattern Implementation
export class DoubleSubmitCSRF {
  private readonly COOKIE_NAME = 'tahta_csrf_cookie';
  private readonly HEADER_NAME = 'X-CSRF-Token';
  private readonly COOKIE_OPTIONS = {
    sameSite: 'strict' as const,
    secure: true,
    httpOnly: false, // Must be false for JavaScript to read it
    path: '/'
  };

  // Generate and set CSRF cookie
  public generateCookie(): string {
    const token = uuidv4();
    this.setCookie(token);
    return token;
  }

  // Set cookie (browser environment)
  private setCookie(token: string): void {
    const expires = new Date();
    expires.setHours(expires.getHours() + 1);
    
    const cookieString = [
      `${this.COOKIE_NAME}=${token}`,
      `expires=${expires.toUTCString()}`,
      `path=${this.COOKIE_OPTIONS.path}`,
      `SameSite=${this.COOKIE_OPTIONS.sameSite}`
    ];
    
    if (this.COOKIE_OPTIONS.secure && window.location.protocol === 'https:') {
      cookieString.push('Secure');
    }
    
    document.cookie = cookieString.join('; ');
  }

  // Get cookie value
  public getCookie(): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === this.COOKIE_NAME) {
        return value;
      }
    }
    return null;
  }

  // Validate token matches cookie
  public validate(headerToken: string): boolean {
    const cookieToken = this.getCookie();
    return cookieToken !== null && cookieToken === headerToken;
  }

  // Add token to request
  public addToRequest(headers: HeadersInit = {}): HeadersInit {
    const token = this.getCookie() || this.generateCookie();
    
    if (headers instanceof Headers) {
      headers.set(this.HEADER_NAME, token);
    } else if (Array.isArray(headers)) {
      headers.push([this.HEADER_NAME, token]);
    } else {
      headers[this.HEADER_NAME] = token;
    }
    
    return headers;
  }
}

export const doubleSubmitCSRF = new DoubleSubmitCSRF();

// Anti-CSRF Meta Tag Management
export function setCSRFMetaTag(token: string): void {
  let metaTag = document.querySelector('meta[name="csrf-token"]');
  
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.setAttribute('name', 'csrf-token');
    document.head.appendChild(metaTag);
  }
  
  metaTag.setAttribute('content', token);
}

export function getCSRFMetaTag(): string | null {
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  return metaTag?.getAttribute('content') || null;
}

// Initialize CSRF protection
export function initializeCSRFProtection(): void {
  // Generate initial token
  const token = csrfTokenManager.getToken();
  if (token) {
    setCSRFMetaTag(token);
  }

  // Also set up double submit cookie
  doubleSubmitCSRF.generateCookie();

  // Refresh token periodically
  setInterval(() => {
    const newToken = csrfTokenManager.refreshToken();
    setCSRFMetaTag(newToken);
    doubleSubmitCSRF.generateCookie();
  }, 55 * 60 * 1000); // Refresh 5 minutes before expiry
}